import { Button } from "@/components/ui/baseUi/button"
import { useAppForm } from "@/components/ui/forms/hooks/useFormHook"
import { errorToastClassName, successToastClassName } from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import { resetPasswordFieldsSchema, type ResetPasswordFields } from "@/modules/auth/authTypes"
import { useLanguage } from "@/modules/language/hooks/useLanguage"
import { getAuthError } from "@modules/auth/authError"
import { AuthContent, AuthForm, AuthHeader, AuthPage, AuthSubmitButton } from "@modules/auth/components/authComponents"
import { LoginBack } from "@modules/auth/components/loginBack"
import { word } from "@modules/core/utils/i18n"
import { Path } from "@modules/navigation/routes"
import { LoggerType } from "@modules/utils/loggerUtils"
import { AuthError as AmplifyAuthError, confirmResetPassword, resetPassword } from "aws-amplify/auth"
import { Navigate, useLocation, useNavigate } from "react-router"
import { toast } from "sonner"

const defaultValues: ResetPasswordFields = {
  code: "",
  newPassword: "",
}

export function ResetPasswordPage() {
  const location = useLocation() as { state?: { email?: string; isRequiredPassword?: boolean } }
  const email = location.state?.email
  const isRequiredPassword = location.state?.isRequiredPassword
  const language = useLanguage()
  const navigate = useNavigate()

  const changePassword = async () => {
    try {
      if (!email) return

      await confirmResetPassword({
        username: email,
        confirmationCode: form.state.values.code,
        newPassword: form.state.values.newPassword,
      })
      void navigate({ pathname: Path.Login }, { state: { email } })
    } catch (e) {
      console.error(LoggerType.Login + "change password", e)
      if (e instanceof AmplifyAuthError) {
        toast.error(getAuthError(e), { classNames: errorToastClassName() })
      } else {
        toast.error(word("global.error.default"), { classNames: errorToastClassName() })
      }
    }
  }

  const resendConfirmationCodeForgotPassword = async () => {
    try {
      if (!email) return

      await resetPassword({ username: email })
      toast.success(word("auth.check_signup.form.notif_code_resend"), { classNames: successToastClassName() })
    } catch (e) {
      console.error(LoggerType.Login + "resend confimation code password", e)
      if (e instanceof AmplifyAuthError) {
        toast.error(getAuthError(e), { classNames: errorToastClassName() })
      } else {
        toast.error(word("global.error.default"), { classNames: errorToastClassName() })
      }
    }
  }

  const form = useAppForm({
    defaultValues,
    validators: {
      onSubmit: resetPasswordFieldsSchema(language),
      onBlur: resetPasswordFieldsSchema(language),
    },
    onSubmit: changePassword,
  })

  if (!email) {
    return <Navigate to={Path.Login} />
  }

  return (
    <AuthPage>
      <AuthForm
        onSubmit={(e) => {
          e.preventDefault()
          void form.handleSubmit()
        }}
        autoComplete="off"
      >
        <AuthHeader
          title={
            isRequiredPassword
              ? word("auth.form.title.reset_password_required")
              : word("auth.form.title.reset_password")
          }
          subtitle={word("auth.reset_password.form.subtitle", { email })}
        />

        <AuthContent>
          <form.AppField
            name="code"
            validators={{
              onBlur: resetPasswordFieldsSchema(language).shape.code,
            }}
          >
            {(field) => (
              <field.Field>
                <field.Content>
                  <field.Input name="code" placeholder={word("auth.check_signup.form.code")} autoComplete="off" />
                  <field.Message />
                </field.Content>
              </field.Field>
            )}
          </form.AppField>

          <form.AppField
            name="newPassword"
            validators={{
              onBlur: resetPasswordFieldsSchema(language).shape.newPassword,
            }}
          >
            {(field) => (
              <field.Field>
                <field.Content>
                  <field.Password
                    inputProps={{
                      name: "newPassword",
                      autoComplete: "new-password",
                      placeholder: word("auth.form.new_password"),
                    }}
                  />
                  <field.Message />
                </field.Content>
              </field.Field>
            )}
          </form.AppField>

          <Button
            variant="underline"
            onClick={() => void resendConfirmationCodeForgotPassword()}
            size="xs"
            className="self-end"
          >
            <Text fontStyle="BS">{word("auth.check_signup.form.resend_code")}</Text>
          </Button>

          <form.Subscribe selector={(state) => [state.isSubmitting]}>
            {([isSubmitting]) => <AuthSubmitButton loading={isSubmitting} buttonLabel={word("global.check")} />}
          </form.Subscribe>

          <LoginBack />
        </AuthContent>
      </AuthForm>
    </AuthPage>
  )
}
